import React, { useState, useRef, useEffect } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import Modal from "@components/ModalTemplate";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useNavigate } from "react-router";
import { Zap, ZapOff, X } from "lucide-react";
import scannerOverlaySrc from "./img/scanner-container.svg";
import "./style.scss";

const QrScannerModal = NiceModal.create(() => {
  const modal = useModal();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const scannerWrapperRef = useRef(null);

  // iOS always delivers the camera stream in landscape; portrait appearance
  // is achieved by object-fit: cover in CSS, not stream constraints.
  const constraints = {
    facingMode: "environment",
    width: { min: 360, ideal: 1080, max: 3840 },
    height: { min: 640, ideal: 1920, max: 2160 },
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const video = scannerWrapperRef.current?.querySelector("video");
      const track = video?.srcObject?.getVideoTracks?.()[0];
      if (!track) return;

      const capabilities = track.getCapabilities?.() ?? {};
      setTorchAvailable(!!capabilities.torch);

      // If the device supports resizeMode (not iOS), force portrait via crop-and-scale.
      // On iOS this capability is absent, so CSS object-fit: cover handles it instead.
      const { width, height } = track.getSettings();
      if (capabilities.resizeMode && width && height && width > height) {
        track
          .applyConstraints({
            resizeMode: "crop-and-scale",
            width: { exact: height },
            height: { exact: width },
            aspectRatio: { exact: 9 / 16 },
          })
          .catch(() => {});
      }

      clearInterval(interval);
    }, 300);
    return () => clearInterval(interval);
  }, []);

  async function toggleTorch() {
    const video = scannerWrapperRef.current?.querySelector("video");
    const track = video?.srcObject?.getVideoTracks?.()[0];
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      // torch not supported on this device
    }
  }

  function handleScan(detectedCodes) {
    if (!detectedCodes.length) return;
    const match = detectedCodes[0].rawValue.match(/\/qrv\/([^/?]+)/);
    if (!match) {
      modal.hide();
      NiceModal.show("qr-error-modal", {
        type: "error_broken_link"
      })
      return;
    }
    sessionStorage.setItem("scanMethod", "app_scanner");
    modal.hide();
    navigate(`/qrv/${match[1]}`);
  }

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => { modal.hide(); navigate("/"); }}
      onAfterClose={() => { modal.remove() }}
      innerClass="qr-scanner-modal"
      disableClose
    >
      <div className="qr-scanner-modal-body">
        <div className="header">
          {torchAvailable && (
            <button
              className={`qr-scanner-modal__torch${torchOn ? " active" : ""}`}
              onClick={toggleTorch}
            >
              {torchOn ? <ZapOff size={16} /> : <Zap size={16} />}
            </button>
          )}
          <button
            className="qr-scanner-modal__close"
            onClick={() => modal.hide()}
          >
            <X size={16} />
          </button>
        </div>
        <div className="scanner-wrapper" ref={scannerWrapperRef}>
          <Scanner
            onScan={handleScan}
            // TODO: handle error or camera opening
            onError={(err) => setError(err?.message ?? "Camera error")}
            formats={["qr_code"]}
            constraints={constraints}
            sound={false}
            components={{ finder: false }}
          >
            <img src={scannerOverlaySrc} alt="" className="qr-scanner-modal__finder" />
          </Scanner>
          {error && <p className="qr-scanner-modal__error">{error}</p>}
        </div>
      </div>
    </Modal>
  );
});

export default QrScannerModal;
